import { MMKV } from 'react-native-mmkv';
import { Platform } from 'react-native';

const STORAGE_KEY_PREFIX = 'episode_purchases_';

const isWeb = Platform.OS === 'web';

let mmkvInstance: MMKV | null = null;

const getMMKV = (): MMKV => {
  if (!mmkvInstance) {
    mmkvInstance = new MMKV({
      id: 'episode-purchases-storage',
    });
  }
  return mmkvInstance;
};

interface PurchaseStorage {
  getString(key: string): string | undefined;
  setString(key: string, value: string): void;
  delete(key: string): void;
  contains(key: string): boolean;
}

const createWebStorage = (): PurchaseStorage => ({
  getString: (key: string): string | undefined => {
    try {
      const value = localStorage.getItem(key);
      return value ?? undefined;
    } catch {
      return undefined;
    }
  },
  setString: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('[EPISODE_PURCHASE] Web localStorage write error:', e);
    }
  },
  delete: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
  contains: (key: string): boolean => {
    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },
});

const createNativeStorage = (): PurchaseStorage => {
  const mmkv = getMMKV();
  return {
    getString: (key: string): string | undefined => mmkv.getString(key),
    setString: (key: string, value: string): void => mmkv.set(key, value),
    delete: (key: string): void => mmkv.delete(key),
    contains: (key: string): boolean => mmkv.contains(key),
  };
};

const purchaseStorage: PurchaseStorage = isWeb
  ? createWebStorage()
  : createNativeStorage();

const getStorageKey = (smartuserId: string): string => {
  return `${STORAGE_KEY_PREFIX}${smartuserId}`;
};

export const getPurchasedEpisodes = (smartuserId: string): Set<string> => {
  if (!smartuserId) {
    console.warn('[EPISODE_PURCHASE] getPurchasedEpisodes called without smartuserId');
    return new Set();
  }

  try {
    const key = getStorageKey(smartuserId);
    const data = purchaseStorage.getString(key);

    if (!data) {
      return new Set();
    }

    const episodeIds: string[] = JSON.parse(data);
    //console.log(`[EPISODE_PURCHASE] Loaded ${episodeIds.length} purchased episodes for user ${smartuserId}`);
    return new Set(episodeIds);
  } catch (error) {
    console.error('[EPISODE_PURCHASE] Error loading purchased episodes:', error);
    return new Set();
  }
};

export const markEpisodeAsPurchased = (smartuserId: string, episodeId: string): void => {
  if (!smartuserId || !episodeId) {
    console.warn('[EPISODE_PURCHASE] markEpisodeAsPurchased called with invalid params:', { smartuserId, episodeId });
    return;
  }

  try {
    const key = getStorageKey(smartuserId);
    const existingEpisodes = getPurchasedEpisodes(smartuserId);

    if (existingEpisodes.has(episodeId)) {
      console.log(`[EPISODE_PURCHASE] Episode ${episodeId} already marked as purchased for user ${smartuserId}`);
      return;
    }

    existingEpisodes.add(episodeId);
    const episodeIds = Array.from(existingEpisodes);
    purchaseStorage.setString(key, JSON.stringify(episodeIds));

    console.log(`[EPISODE_PURCHASE] Marked episode ${episodeId} as purchased for user ${smartuserId}`);
    console.log(`[EPISODE_PURCHASE] Total purchased episodes: ${episodeIds.length}`);
  } catch (error) {
    console.error('[EPISODE_PURCHASE] Error marking episode as purchased:', error);
  }
};

export const isEpisodePurchased = (smartuserId: string, episodeId: string): boolean => {
  if (!smartuserId || !episodeId) {
    return false;
  }

  try {
    const purchasedEpisodes = getPurchasedEpisodes(smartuserId);
    const isPurchased = purchasedEpisodes.has(episodeId);

    if (isPurchased) {
      console.log(`[EPISODE_PURCHASE] Episode ${episodeId} is already purchased by user ${smartuserId}`);
    }

    return isPurchased;
  } catch (error) {
    console.error('[EPISODE_PURCHASE] Error checking if episode is purchased:', error);
    return false;
  }
};

export const clearPurchasedEpisodes = (smartuserId: string): void => {
  if (!smartuserId) {
    console.warn('[EPISODE_PURCHASE] clearPurchasedEpisodes called without smartuserId');
    return;
  }

  try {
    const key = getStorageKey(smartuserId);
    purchaseStorage.delete(key);
    console.log(`[EPISODE_PURCHASE] Cleared all purchased episodes for user ${smartuserId}`);
  } catch (error) {
    console.error('[EPISODE_PURCHASE] Error clearing purchased episodes:', error);
  }
};

export const getPurchasedEpisodesCount = (smartuserId: string): number => {
  if (!smartuserId) {
    return 0;
  }

  try {
    const purchasedEpisodes = getPurchasedEpisodes(smartuserId);
    return purchasedEpisodes.size;
  } catch (error) {
    console.error('[EPISODE_PURCHASE] Error getting purchased episodes count:', error);
    return 0;
  }
};

export const episodePurchaseStorage = {
  getPurchasedEpisodes,
  markEpisodeAsPurchased,
  isEpisodePurchased,
  clearPurchasedEpisodes,
  getPurchasedEpisodesCount,
};
